import {useQueryClient} from '@tanstack/react-query';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import {Role, User} from '../../../incase-domain/user';
import {transformZodErrors} from '../../../incase-domain/validators/transformZodErrors';
import {createUserValidator} from '../../../incase-domain/validators/user/createUser';
import {loggedInUserSelector} from '../../../store/selectors/auth.selectors';
import {UserConfigType, UserForm, UserFormType} from './UserForm';
import Modal from '../../../incase-domain/components/modals/directory/Modal';
import {type TypedTranslation} from '../../../i18n';
import {useRegisterUserMutation} from './utils/queries';

const CREATE_USER_FORM_ID = 'create_user_form';

interface CreateUserModalProps {
    isOpen: boolean;
    onCancel: () => void;
}

export function CreateUserModal({isOpen, onCancel}: CreateUserModalProps): JSX.Element {
    const {t} = useTranslation();

    return (
        <Modal
            isOpen={isOpen}
            header={t('registerPage.createUser')}
            showCancel
            cancelText={t('common.dismiss')}
            onCancel={onCancel}
            showConfirm
            confirmText={t('registerPage.create')}
            formId={CREATE_USER_FORM_ID}
            size="md"
            dataCy="create_user_modal"
        >
            <CreateUserForm onClose={onCancel} />
        </Modal>
    );
}

function CreateUserForm({onClose}: {onClose: () => void}): JSX.Element {
    const client = useQueryClient();
    const {t} = useTranslation();

    const [errors, setErrors] = useState<Record<string, string[]>>({});
    const loggedInUser = useSelector(loggedInUserSelector);

    const [user, setUser] = useState({
        roles: [Role.VIEWER],
        username: '',
        email: '',
        password: '',
        passwordExpirationTime: 100,
    });

    const [userConfigState, setUserConfigState] = useState({
        maxNumberOfPresentations: undefined,
        maxNumberOfRooms: undefined,
        maxPresentationSize: undefined,
        maxNumberOfUsers: undefined,
        maxGptUsage: undefined,
    } as UserConfigType);

    const registerMutation = useRegisterUserMutation({
        onSuccess: (data) => {
            if (data.success) {
                client.invalidateQueries({queryKey: QUERY_KEYS.user.lists()});
                toast.success(`${t('toasts.userSuccessfullyCreated')}`);
                onClose();
            }

            if (!data.success && data.errors) {
                if (data.errors.message && data.errors.message.includes('maxNumberOfUsers')) {
                    toast.error(`${t(data.errors.message[0] as TypedTranslation)}`);
                    resetForm();
                }
                setErrors(data.errors);
            } else {
                resetForm();
            }
        },
    });

    function createUser(): void {
        const validation = createUserValidator.safeParse(user);

        if (validation.success) {
            setErrors({});
            registerMutation.mutate({
                user: {
                    ...user,
                    isAnonymous: true,
                    adminId: (loggedInUser as User)._id,
                },
                userConfig: {...userConfigState},
            });
        } else {
            setErrors(transformZodErrors(validation.error.errors));
        }
    }

    function resetForm(): void {
        setUser({
            username: '',
            email: '',
            password: '',
            passwordExpirationTime: 100,
            roles: [Role.VIEWER],
        });
        setUserConfigState({
            maxNumberOfUsers: undefined,
            maxPresentationSize: undefined,
            maxNumberOfPresentations: undefined,
            maxNumberOfRooms: undefined,
            maxGptUsage: undefined,
        });
        setErrors({});
    }

    return (
        <UserForm
            id={CREATE_USER_FORM_ID}
            type={UserFormType.REGISTER}
            user={user}
            setUser={setUser}
            userConfigState={userConfigState}
            setUserConfigState={setUserConfigState}
            errors={errors}
            loggedInUser={loggedInUser}
            spinner={registerMutation.isLoading}
            onSubmit={createUser}
        />
    );
}
